package com.oasisfeng.invaluable.social

import com.oasisfeng.invaluable.NestedLinkable
import com.oasisfeng.invaluable.actor.Actor
import com.oasisfeng.invaluable.actor.ActorRoute
import com.oasisfeng.invaluable.actor.Commitment
import com.oasisfeng.invaluable.actor.CommitmentRendering
import com.oasisfeng.invaluable.actor.CommitmentSnapshot
import com.oasisfeng.invaluable.actor.ObservedCommitment
import com.oasisfeng.invaluable.actor.VisibleCommitment
import com.oasisfeng.invaluable.data.LinkProjection
import com.oasisfeng.invaluable.data.LinkProjector
import com.oasisfeng.invaluable.data.LinkView
import com.oasisfeng.invaluable.data.Observation
import com.oasisfeng.invaluable.data.ProjectedLinkRecordResolver
import com.oasisfeng.invaluable.data.memoizing
import com.oasisfeng.invaluable.dsl.Post
import com.oasisfeng.invaluable.dsl.ProfileEvidence
import com.oasisfeng.invaluable.dsl.SocialDsl
import com.oasisfeng.invaluable.dsl.profileOf
import com.oasisfeng.invaluable.event.ReplicatedEventStore

/**
 * A node's "local world": only what it can see right now, with missing material represented honestly.
 *
 * The view is rebuilt from accepted attestations. Each head is observed through [ObservedCommitment]:
 *
 * - When the full topology is locally resolvable, the commitment joins [snapshot] and is rendered into posts/routes
 *   through the same [SocialDsl] render path the model defines.
 * - When a committed body was withheld, the head stays in [missing] as a [ObservedCommitment] — issuer known, body a
 *   [com.oasisfeng.invaluable.data.Observation.Missing] — never a synthesized placeholder.
 *
 * Forks need no special handling: an actor's multiple heads are simply multiple commitments in [snapshot].
 */
class LocalSocialView internal constructor(
    val localActor: Actor,
    /** Completely-resolvable commitments across every known actor. */
    val snapshot: CommitmentSnapshot,
    /** Heads accepted but not fully visible: issuer known, some preimage (typically the body) withheld. */
    val missing: List<ObservedCommitment>,
    /**
     * Content projections this node custodies a sealed ciphertext for (Milestone 13). A [missing] head whose
     * [contentProjection][ObservedCommitment.contentProjection] is in this set is dark *and sealed* — present-but-locked
     * (no key held), not merely omitted — which [SocialBoard] surfaces distinctly.
     */
    val sealedContentProjections: Set<LinkProjection> = emptySet(),
) {
    /** Social items recovered from complete commitments via the model's render path. */
    val rendering: CommitmentRendering<LinkView> = SocialDsl.renderSocialCommitments(snapshot)

    /** Distinct visible posts. */
    fun posts(): List<Post> = rendering.visible.mapNotNull { it.target as? Post }.distinct()

    /** Attention routes addressed to [actor] (recommendations / deliveries to me). */
    fun inbox(actor: Actor = localActor): List<VisibleCommitment<ActorRoute>> =
        rendering.visible.mapNotNull { visible ->
            (visible.target as? ActorRoute)?.takeIf { it.recipient == actor }?.let { VisibleCommitment(visible.commitment, it) }
        }

    /** Direct comments/replies whose immediate cause is [post]. */
    fun replies(post: Post): List<Post> =
        rendering.visible.mapNotNull { it.target as? Post }.filter { it.cause() == post.link }

    /** Backers of [target]: who committed, and how much, in this snapshot. */
    fun backers(target: NestedLinkable): List<Commitment> = snapshot.commitmentsOf(target.link)

    /** Evidence for every committed direct expression currently known under [actor]'s public meaning space. */
    fun profile(actor: Actor): List<ProfileEvidence> = snapshot.profileOf(actor)

    companion object {
        internal fun from(
            localActor: Actor,
            projector: LinkProjector,
            store: ReplicatedEventStore,
            records: ProjectedLinkRecordResolver = store.resolver(),
        ): LocalSocialView {
            val complete = mutableListOf<Commitment>()
            val missing = mutableListOf<ObservedCommitment>()
            // Every head in an actor's log chains through its predecessors, so one memo for the whole pass turns
            // rebuilding this view from quadratic in the log into linear. It dies with the pass — see [memoizing].
            val resolver = records.memoizing()
            for (attestation in store.attestations()) {
                val observed = ObservedCommitment(projector, attestation.head, resolver)
                when (val commitment = observed.complete) {
                    is Observation.Known -> complete += commitment.value
                    else -> missing += observed
                }
            }
            val sealed = store.sealedContents().mapTo(mutableSetOf()) { it.contentProjection }
            return LocalSocialView(localActor, CommitmentSnapshot(complete), missing, sealed)
        }
    }
}
